/**
 * Vista Ranking MULTI-TROFEO — 4 podios simultáneos entre hermanos:
 *   👑 Aquisição · 💰 Valor · ⭐ Qualidade · 🎯 Conversão.
 * Cada promotor brilla en algo (emoción). Copy épico NEUTRO de género (verbos:
 * reina/domina/lidera/conquista; títulos: lenda/mestre/fera/imbatível).
 * Read-only. Consume GET /api/cashier/ranking-trofeos?desde=&hasta=.
 */

import { t } from '../languages.js';
import { rangoPorDefecto } from './_helpers.js';
import { renderChipsFecha } from './chips-fecha.js';
import { celebrarLogro, mejoroRespectoAGuardado } from '../systems/motion.js';
import { montarLottie } from '../systems/lottie-icon.js';

// Lottie de copa rotante (nm:"Trophy"). Se monta SOLO en las dimensiones donde el
// promotor está en el podio (top-3): premia con animación premium su logro.
const LOTTIE_COPA_SRC = '/assets/lottie/ranking.json';

function esc(s) {
    return String(s == null ? '' : s).replace(
        /[&<>"']/g,
        c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]
    );
}

function medalla(pos) {
    return pos === 1 ? '🥇' : pos === 2 ? '🥈' : pos === 3 ? '🥉' : `#${pos}`;
}

// Definición de los 4 trofeos: clave (= key del backend), icono, color, título
// épico neutro del #1, verbo de acción neutro, y cómo formatear el valor.
const TROFEOS = [
    { key: 'general', icono: '🏆', color: '#fbbf24', titulo: 'CAMPEÃO', fmt: v => v + ' pts' },
    { key: 'valor', icono: '💰', color: '#22c55e', titulo: 'MESTRE', fmt: v => 'R$ ' + v.toLocaleString('pt-BR') },
    { key: 'peixe', icono: '🐟', color: '#a855f7', titulo: 'PESCADOR', fmt: v => 'R$ ' + v.toLocaleString('pt-BR') },
    { key: 'retencao', icono: '🔥', color: '#ef4444', titulo: 'GUARDIÃO', fmt: v => v + '%' },
    { key: 'conversao', icono: '🎯', color: '#14b8a6', titulo: 'IMBATÍVEL', fmt: v => v + '%' },
    { key: 'aquisicao', icono: '👑', color: '#f5a623', titulo: 'LENDA', fmt: v => v },
];

export function renderRanking({ apiGet }) {
    const el = document.createElement('div');
    const { desde, hasta } = rangoPorDefecto();

    el.innerHTML = `
        <div class="card">
            <h2 style="margin:0 0 2px;">${t('ranking.titulo')}</h2>
            <p style="color:var(--text-dim); font-size:var(--fs-label); margin:0 0 6px; line-height:1.15;">${t('ranking.descripcion')}</p>
            <div class="date-range">
                <input type="date" id="pm-desde" value="${desde}" aria-label="${t('common.desde')}">
                <input type="date" id="pm-hasta" value="${hasta}" aria-label="${t('common.hasta')}">
            </div>
            <div id="pm-ranking-body">
                <div style="text-align:center; color:var(--text-dim); padding:20px;">${t('common.cargando')}</div>
            </div>
        </div>`;

    const body = el.querySelector('#pm-ranking-body');
    const desdeEl = el.querySelector('#pm-desde');
    const hastaEl = el.querySelector('#pm-hasta');

    const chips = renderChipsFecha({ desdeEl, hastaEl, onChange: () => cargar() });
    desdeEl.closest('.date-range').insertAdjacentElement('beforebegin', chips);

    // Una fila del ranking (posición + nombre + valor), resalta si es mía.
    // Espacio vertical al mínimo: line-height 1, padding casi nulo.
    function renderFila(p, trofeo) {
        const yo = p.esYo ? `background:${trofeo.color}1f; border-radius:5px;` : '';
        const nombre = p.esYo ? t('ranking.tu') : esc(p.nombre);
        return `<div style="display:flex; align-items:center; gap:5px; padding:2px 5px; line-height:1.1; ${yo}">
            <span style="min-width:20px; text-align:center;">${medalla(p.posicion)}</span>
            <span style="flex:1; min-width:0; font-weight:${p.esYo ? '700' : '500'}; font-size:13px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap;">${nombre}</span>
            <strong style="flex-shrink:0; color:${trofeo.color}; font-size:13px;">${trofeo.fmt(p.valor)}</strong>
        </div>`;
    }

    function renderColumna(trofeo, top3, miPos, vecinos) {
        const lider = top3[0];
        const liderEsYo = lider && lider.esYo;
        // Frase épica en DOS líneas: nombre arriba, "reina na aquisição" abajo.
        // Centrada, compacta (line-height 1.05, sin márgenes).
        const fraseLider = lider
            ? `<div style="font-size:11px; color:${trofeo.color}; font-weight:700; text-align:center; line-height:1.05;">
                   <div style="overflow:hidden; text-overflow:ellipsis; white-space:nowrap;">${liderEsYo ? t('ranking.tu') : esc(lider.nombre)}</div>
                   <div style="font-weight:600;">${t('ranking.verbo.' + trofeo.key)}</div>
               </div>`
            : '';

        const filas = top3.map(p => renderFila(p, trofeo)).join('');

        // Pie: en podio → mensaje épico. Fuera → mi vecindario (arriba + yo + abajo).
        let pie = '';
        if (miPos === 1) {
            pie = `<span style="color:${trofeo.color}; font-weight:700;">${t('ranking.vocedomina')} 👑</span>`;
        } else if (miPos && miPos <= 3) {
            pie = `<span style="color:${trofeo.color};">${t('ranking.nopodio')} 🔥</span>`;
        } else if (miPos && vecinos && vecinos.length) {
            const sep = `<div style="text-align:center; color:var(--text-dim); font-size:10px; line-height:0.7;">⋮</div>`;
            pie = `<div style="color:var(--text-dim); font-size:10px; text-align:center; line-height:1;">${t('ranking.destronar').replace('{n}', miPos - 1)}</div>
                   ${sep}${vecinos.map(p => renderFila(p, trofeo)).join('')}`;
        }

        return `
            <div style="min-width:0; overflow:hidden; border:1px solid var(--border); border-radius:9px; padding:6px;">
                <div style="text-align:center; line-height:1;">
                    ${
                        miPos && miPos <= 3
                            ? `<div class="pm-rank-lottie" data-copa-podio style="width:38px;height:38px;margin:0 auto;">${trofeo.icono}</div>`
                            : `<div style="font-size:20px; line-height:1;">${trofeo.icono}</div>`
                    }
                    <span style="font-size:10px; letter-spacing:0.3px; text-transform:uppercase; color:var(--text-dim);">${t('ranking.trofeo.' + trofeo.key)}</span>
                    <span style="font-size:10px; font-weight:800; letter-spacing:0.5px; color:${trofeo.color};"> · ${trofeo.titulo}</span>
                </div>
                ${fraseLider}
                ${filas || `<div style="text-align:center; color:var(--text-dim); font-size:12px; padding:4px;">${t('ranking.sinDatos')}</div>`}
                ${pie ? `<div style="margin-top:4px; padding-top:4px; border-top:1px solid var(--border); font-size:11px; text-align:center; line-height:1.1;">${pie}</div>` : ''}
            </div>`;
    }

    let cargando = false;
    let copasMontadas = []; // handles de los lotties de copa, para destruir al recargar
    function destruirCopas() {
        for (const h of copasMontadas) {
            try {
                h.destroy();
            } catch (_) {}
        }
        copasMontadas = [];
    }
    async function cargar() {
        if (cargando) return;
        cargando = true;
        destruirCopas(); // limpiar lotties de la corrida anterior (evita leak de rAF)
        body.innerHTML = `<div style="text-align:center; color:var(--text-dim); padding:24px;">${t('common.cargando')}</div>`;
        const r = await apiGet(`/api/cashier/ranking-trofeos?desde=${desdeEl.value}&hasta=${hastaEl.value}`);
        cargando = false;
        if (!r || r.ok === false) {
            body.innerHTML = `<div style="text-align:center; color:var(--danger); padding:24px;">${(r && r.mensaje) || t('common.reintentar')}</div>`;
            return;
        }
        const trofeos = r.trofeos || {};
        const miPosicion = r.miPosicion || {};
        const vecindario = r.vecindario || {};
        const hayDatos = Object.values(trofeos).some(arr => arr && arr.length);
        if (!hayDatos) {
            body.innerHTML = `<div style="text-align:center; color:var(--text-dim); padding:24px;">${t('ranking.sinDatos')}</div>`;
            return;
        }

        body.innerHTML = `
            <div style="display:grid; grid-template-columns:repeat(2,1fr); gap:6px;">
                ${TROFEOS.map(tr => renderColumna(tr, trofeos[tr.key] || [], miPosicion[tr.key], vecindario[tr.key])).join('')}
            </div>`;

        // Montar la copa rotante en las cards de podio (el emoji ya impreso queda de
        // fallback si el lottie no carga). Post-render: los nodos ya están en el DOM.
        for (const cont of body.querySelectorAll('[data-copa-podio]')) {
            copasMontadas.push(montarLottie(cont, { src: LOTTIE_COPA_SRC, svgFallback: cont.innerHTML }));
        }

        // Celebrar si mejoró en CUALQUIER trofeo (subió de puesto).
        for (const tr of TROFEOS) {
            const pos = miPosicion[tr.key];
            if (pos && mejoroRespectoAGuardado('rank_' + tr.key, pos, 'bajo')) {
                celebrarLogro(body, pos <= 3 ? 'oro' : 'verde');
                break;
            }
        }
    }

    desdeEl.addEventListener('change', cargar);
    hastaEl.addEventListener('change', cargar);
    cargar();

    return el;
}
